import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, getErrMsg } from "../lib/api";
import { logError } from "../lib/logger";
import { useAuth } from "../context/AuthContext";
import { formatDateTime } from "../lib/format";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { ImportWizard } from "../components/ImportWizard";
import { UserManagement, RolBadge } from "../components/UserManagement";
import {
  FileSpreadsheet,
  Upload,
  Loader2,
  Mail,
  Send,
  CalendarClock,
} from "lucide-react";


export default function AyarlarPage() {
  const { user } = useAuth();
  const isYonetici = user.role === "yonetici";

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="space-y-6"
    >
      {/* Own profile */}
      <div className="rounded-xl border bg-card p-5 sm:p-6 shadow-[0_1px_0_rgba(14,27,46,0.06),0_10px_30px_rgba(14,27,46,0.06)]">
        <h2 className="font-display text-lg font-semibold mb-4">Hesabım</h2>
        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase">Ad Soyad</p>
            <p className="mt-1 text-sm font-medium" data-testid="profile-name">
              {user.ad_soyad || "—"}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase">E-posta</p>
            <p className="mt-1 text-sm font-medium" data-testid="profile-email">{user.email}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase">Rol</p>
            <div className="mt-1">
              <RolBadge role={user.role} testid="profile-role-badge" />
            </div>
          </div>
        </div>
      </div>

      {isYonetici ? (
        <>
          <UserManagement currentUser={user} />
          <EpostaBildirimSection />
          <div
            className="rounded-xl border bg-card p-5 sm:p-6 shadow-[0_1px_0_rgba(14,27,46,0.06),0_10px_30px_rgba(14,27,46,0.06)]"
            data-testid="veri-ice-aktarma-section"
          >
            <div className="flex items-center gap-2 mb-1">
              <Upload className="h-5 w-5 text-[hsl(var(--accent))]" />
              <h2 className="font-display text-lg font-semibold">Veri İçe Aktarma</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              XLSX/CSV dosyalarından Ciro, Gider, Maliyet, Bütçe Planı ve Ücret Geçmişi verilerini kolon eşleme
              sihirbazıyla içe aktarın. Her aktarım geri alınabilir.
            </p>
            <ImportWizard />
          </div>
          <ExcelImportSection />
        </>
      ) : (
        <div className="rounded-xl border bg-card p-5 sm:p-6">
          <p className="text-sm text-muted-foreground">
            Kullanıcı yönetimi yalnızca Yönetici rolündeki kullanıcılara açıktır.
          </p>
        </div>
      )}
    </motion.div>
  );
}


function EpostaBildirimSection() {
  const [durum, setDurum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const fetchDurum = useCallback(async () => {
    try {
      const res = await api.get("/eposta/durum");
      setDurum(res.data);
    } catch (err) {
      logError("E-posta durumu yüklenemedi:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDurum();
  }, [fetchDurum]);

  const handleTest = async () => {
    setSending(true);
    try {
      await api.post("/eposta/haftalik-test");
      toast.success("Test e-postası gönderildi. Gelen kutunuzu kontrol edin.");
      fetchDurum();
    } catch (err) {
      toast.error(getErrMsg(err, "E-posta gönderilemedi."));
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="rounded-xl border bg-card p-5 sm:p-6 shadow-[0_1px_0_rgba(14,27,46,0.06),0_10px_30px_rgba(14,27,46,0.06)]"
      data-testid="eposta-bildirim-section"
    >
      <div className="flex items-center gap-2 mb-1">
        <Mail className="h-5 w-5 text-[hsl(var(--accent))]" />
        <h2 className="font-display text-lg font-semibold">Haftalık E-posta Özeti</h2>
      </div>
      <p className="text-sm text-muted-foreground">
        Haftalık AI finansal özeti her Pazartesi sabahı yöneticilere otomatik e-posta ile gönderilir.
      </p>

      {loading ? (
        <div className="mt-4 space-y-2">
          <Skeleton className="h-14 w-full" />
        </div>
      ) : durum ? (
        <>
          <div className="mt-4 grid gap-3 sm:grid-cols-3" data-testid="eposta-durum-bilgi">
            <div className="rounded-lg bg-muted/60 p-3">
              <p className="text-[10px] font-medium text-muted-foreground uppercase">Gönderici</p>
              <p className="mt-0.5 text-sm font-medium break-all" data-testid="eposta-gonderici">{durum.gonderici || "—"}</p>
            </div>
            <div className="rounded-lg bg-muted/60 p-3">
              <p className="text-[10px] font-medium text-muted-foreground uppercase">Alıcılar</p>
              <p className="mt-0.5 text-sm font-medium break-all" data-testid="eposta-alicilar">
                {(durum.alicilar || []).join(", ") || "—"}
              </p>
            </div>
            <div className="rounded-lg bg-muted/60 p-3">
              <p className="text-[10px] font-medium text-muted-foreground uppercase">Zamanlama</p>
              <p className="mt-0.5 flex items-center gap-1.5 text-sm font-medium" data-testid="eposta-zamanlama">
                <CalendarClock className="h-3.5 w-3.5 text-[hsl(var(--accent))]" />
                {durum.zamanlama}
              </p>
            </div>
          </div>

          {durum.son_gonderim && (
            <p className="mt-3 text-xs text-muted-foreground" data-testid="eposta-son-gonderim">
              Son gönderim: {formatDateTime(durum.son_gonderim.tarih)} •{" "}
              {durum.son_gonderim.tetik === "otomatik" ? "otomatik" : "manuel test"} •{" "}
              {durum.son_gonderim.durum === "basarili" ? (
                <span className="font-semibold text-emerald-600">başarılı</span>
              ) : (
                <span className="font-semibold text-red-600">hata: {durum.son_gonderim.hata}</span>
              )}
            </p>
          )}

          <div className="mt-4">
            <Button
              onClick={handleTest}
              disabled={sending || !durum.yapilandirildi}
              className="min-h-[44px] gap-2 transition-colors duration-150 active:scale-[0.98]"
              data-testid="eposta-test-button"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {sending ? "Gönderiliyor…" : "Test E-postası Gönder"}
            </Button>
            {!durum.yapilandirildi && (
              <p className="mt-2 text-xs text-red-600">E-posta ayarları eksik. Sistem yöneticinizle iletişime geçin.</p>
            )}
          </div>
        </>
      ) : (
        <p className="mt-4 text-sm text-muted-foreground">E-posta durumu yüklenemedi.</p>
      )}
    </div>
  );
}

function ExcelImportSection() {
  const [durum, setDurum] = useState(null);
  const [importing, setImporting] = useState(false);
  const [confirmForce, setConfirmForce] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const fileRef = useRef(null);

  const fetchDurum = useCallback(async () => {
    try {
      const res = await api.get("/import/durum");
      setDurum(res.data);
    } catch (err) {
      logError("İçe aktarım durumu yüklenemedi:", err);
    }
  }, []);

  useEffect(() => {
    fetchDurum();
  }, [fetchDurum]);

  const runImport = async (file, force = false) => {
    setImporting(true);
    try {
      let res;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        res = await api.post(`/import/excel?force=${force}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        res = await api.post(`/import/excel?force=${force}`);
      }
      toast.success(res.data.message);
      if (res.data.uyarilar?.length > 0) {
        toast.warning(`${res.data.uyarilar.length} uyarı oluştu. İlki: ${res.data.uyarilar[0]}`);
      }
      fetchDurum();
    } catch (err) {
      if (err?.response?.status === 409) {
        setPendingFile(file || null);
        setConfirmForce(true);
      } else {
        toast.error(getErrMsg(err, "Aktarım yapılamadı."));
      }
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const aktarilan = durum?.aktarilan;
  const toplam = aktarilan ? aktarilan.ciro + aktarilan.gider + aktarilan.maliyet : 0;

  return (
    <div
      className="rounded-xl border bg-card p-5 sm:p-6 shadow-[0_1px_0_rgba(14,27,46,0.06),0_10px_30px_rgba(14,27,46,0.06)]"
      data-testid="excel-import-section"
    >
      <div className="flex items-center gap-2 mb-1">
        <FileSpreadsheet className="h-5 w-5 text-[hsl(var(--accent))]" />
        <h2 className="font-display text-lg font-semibold">Veri Aktarımı (Excel)</h2>
      </div>
      <p className="text-sm text-muted-foreground">
        Mevcut Excel dosyanızdaki (3A Finansal Performans Sistemi) Ciro, Gider ve Maliyet
        kayıtlarını tek tıkla sisteme aktarın.
      </p>

      {aktarilan && (
        <div className="mt-4 grid grid-cols-3 gap-3 max-w-md" data-testid="excel-import-status">
          {[
            { label: "Ciro", value: aktarilan.ciro },
            { label: "Gider", value: aktarilan.gider },
            { label: "Maliyet", value: aktarilan.maliyet },
          ].map((s) => (
            <div key={s.label} className="rounded-lg bg-muted/60 p-3 text-center">
              <p className="text-[10px] font-medium text-muted-foreground uppercase">{s.label}</p>
              <p className="mt-0.5 font-display text-lg font-bold tabular-nums">
                {s.value.toLocaleString("tr-TR")}
              </p>
            </div>
          ))}
        </div>
      )}
      {durum?.son_aktarim && (
        <p className="mt-2 text-xs text-muted-foreground">
          Son aktarım: {formatDateTime(durum.son_aktarim.tarih)} • {durum.son_aktarim.kullanici}
        </p>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button
          onClick={() => runImport(null)}
          disabled={importing}
          className="min-h-[44px] gap-2 transition-colors duration-150 active:scale-[0.98]"
          data-testid="excel-import-button"
        >
          {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
          {importing ? "Aktarılıyor…" : toplam > 0 ? "Yeniden Aktar" : "Excel'den İçe Aktar"}
        </Button>
        <Button
          variant="outline"
          disabled={importing}
          onClick={() => fileRef.current?.click()}
          className="min-h-[44px] gap-2"
          data-testid="excel-import-upload-button"
        >
          <Upload className="h-4 w-4" />
          Farklı Dosya Yükle
        </Button>
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx"
          className="hidden"
          data-testid="excel-import-file-input"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) runImport(f);
          }}
        />
      </div>

      <AlertDialog open={confirmForce} onOpenChange={setConfirmForce}>
        <AlertDialogContent className="bg-card">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Üzerine Yazılsın mı?</AlertDialogTitle>
            <AlertDialogDescription>
              Daha önce Excel aktarımı yapılmış. Devam ederseniz önceki Excel kayıtları
              silinip yeniden aktarılacak. Elle girdiğiniz kayıtlar etkilenmez.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="min-h-[44px]">Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmForce(false);
                runImport(pendingFile, true);
              }}
              className="min-h-[44px]"
              data-testid="excel-import-force-confirm-button"
            >
              Üzerine Yaz ve Aktar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
